import React from 'react';
import { GlobeIcon, SmartphoneIcon, UserIcon, MicIcon, SparklesIcon, ArrowUpIcon, MessageCircleIcon } from 'lucide-react';
import { useLanguage } from '../../LanguageContext';
import { WebsiteLaptop, PortalScreen, AppPhone } from '../DigitalDevices';
import { ATNA_LOGO, dxEntries, dxLlm, dxAtnaLayer, type Bi, type EntryKey } from '../../../data/digitalExperienceChallenge';
const PORTAL_STATS = [{
  value: '3',
  label: {
    fr: 'Demandes en cours',
    en: 'Open requests'
  }
}, {
  value: '2',
  label: {
    fr: 'Notifications',
    en: 'Notifications'
  }
}];
export const ENTRY_ICONS: Record<EntryKey, typeof GlobeIcon> = {
  web: GlobeIcon,
  app: SmartphoneIcon,
  portal: UserIcon,
  llm: MicIcon
};
function usePick() {
  const {
    lang
  } = useLanguage();
  return (b: Bi) => lang === 'FR' ? b.fr : b.en;
}
export function AskBar({
  width
}: {
  width?: number;
}) {
  const pick = usePick();
  return <div className="h-[60px] rounded-full bg-white border border-[#ECEBFA] shadow-[0_18px_40px_-20px_rgba(79,70,229,0.45)] flex items-center gap-3 pl-6 pr-2" style={{
    width
  }} data-id="element-3020" data-mp="srs3ye6f6e5d">
      <SparklesIcon className="w-5 h-5 text-[#4F46E5] shrink-0" strokeWidth={1.8} aria-hidden="true" data-id="element-3021" data-mp="3b8iphhfnpnoz" />
      <span className="flex-1 font-sans text-[16px] text-[#4A5268] truncate" data-id="element-3022" data-mp="3g7dkriut94pz">{pick(dxAtnaLayer.ask)}</span>
      <span className="w-11 h-11 rounded-full bg-[#4F46E5] flex items-center justify-center shrink-0" data-id="element-3023" data-mp="3p67d2x13z8xq">
        <ArrowUpIcon className="w-5 h-5 text-white" strokeWidth={2.4} aria-hidden="true" data-id="element-3024" data-mp="3u5bkpj8dwyly" />
      </span>
    </div>;
}
export function AtnaLayer({
  width
}: {
  width?: number;
}) {
  const pick = usePick();
  return <div className="rounded-[32px] bg-white border border-[#DCD9FA] shadow-[0_22px_50px_-24px_rgba(79,70,229,0.55)] px-7 py-5 text-center" style={{
    width
  }} data-id="element-3025" data-mp="7p7cy08u06j7">
      <img src={ATNA_LOGO} alt="ATNA" className="h-9 w-auto object-contain mx-auto" data-id="element-3026" data-mp="btf3m2yunwk2" />
      <p className="mt-2 font-sans text-[14.5px] font-medium text-[#4F46E5]" data-id="element-3027" data-mp="gryoe01pj2au">{pick(dxAtnaLayer.verbs)}</p>
    </div>;
}
export function EntryLabel({
  entryKey,
  width = 214
}: {
  entryKey: EntryKey;
  width?: number;
}) {
  const pick = usePick();
  const entry = dxEntries.find(e => e.key === entryKey)!;
  const Icon = ENTRY_ICONS[entryKey];
  return <div className="relative flex flex-col items-center" style={{
    width
  }} data-id="element-3028" data-mp="2ri6hzzo34fej">
      <span className="relative z-10 w-[54px] h-[54px] rounded-full bg-white border border-[#ECEBFA] shadow-[0_10px_24px_-14px_rgba(79,70,229,0.5)] flex items-center justify-center" data-id="element-3029" data-mp="2rk9hdab4nkvx">
        <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-3030" data-mp="30l7lrtph9uf1" />
      </span>
      <div className="-mt-5 w-full rounded-2xl bg-white/85 border border-[#F0F0F7] pt-7 pb-3 px-3 text-center" data-id="element-3031" data-mp="2wjm1hnam6dut">
        <p className="font-sans text-[15px] font-bold tracking-[0.04em] uppercase text-[#4F46E5]" data-id="element-3032" data-mp="3aey5ymekniox">{pick(entry.label)}</p>
        <p className="font-sans text-[13px] leading-snug text-[#5B6478] mt-0.5" data-id="element-3033" data-mp="1g31dqr8b28st">{pick(entry.text)}</p>
      </div>
    </div>;
}
export function LlmPanel() {
  const pick = usePick();
  return <div className="w-[190px] space-y-2.5" data-id="element-3034" data-mp="3mrtklmqwwizr">
      <div className="rounded-2xl bg-[#6D66F0] px-4 py-3 font-sans text-[14px] leading-snug text-white" data-id="element-3035" data-mp="3ms1wpcf7nk9w">{pick(dxLlm.intent)}</div>
      <div className="rounded-2xl bg-white border border-[#EEF0F4] px-3 py-2.5 flex items-center justify-between" data-id="element-3036" data-mp="3rsh8hzjugjpr">
        {dxLlm.assistants.map(a => <span key={a} className="flex flex-col items-center gap-1" data-id="element-3037" data-mp="3hzs1bgvbts08">
            <span className="w-7 h-7 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-3038" data-mp="340iv9a0u4e9z">
              <MessageCircleIcon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-3039" data-mp="390oec1t1gsgg" />
            </span>
            <span className="font-sans text-[9px] text-[#5B6478]" data-id="element-3040" data-mp="2u462ced464ga">{a}</span>
          </span>)}
      </div>
      {dxLlm.questions.map(q => <div key={q.fr} className="rounded-xl bg-white border border-[#EEF0F4] px-3 py-2 font-sans text-[11px] leading-snug text-[#4A5268]" data-id="element-3041" data-mp="sbhvlv19gyvr">{pick(q)}</div>)}
    </div>;
}
function Scaled({
  scale,
  w,
  h,
  children
}: {
  scale: number;
  w: number;
  h: number;
  children: React.ReactNode;
}) {
  return <div style={{
    width: w * scale,
    height: h * scale
  }} data-id="element-3042" data-mp="2pc5k4i6l52mz">
      <div className="origin-top-left" style={{
      width: w,
      transform: `scale(${scale})`
    }} data-id="element-3043" data-mp="2pcnef99ot6s9">{children}</div>
    </div>;
}

// Connector geometry for the 1000 × 700 canvas.
function Connectors() {
  const paths = ['M320 30 C 230 40, 180 70, 180 108', 'M430 60 C 430 80, 425 90, 425 108', 'M560 60 C 570 90, 640 80, 640 108', 'M680 30 C 790 36, 880 70, 890 108', 'M180 488 C 190 560, 300 570, 360 610', 'M425 555 C 425 580, 430 590, 440 600', 'M640 520 C 640 560, 600 590, 580 600', 'M890 580 C 870 600, 760 610, 720 625'];
  const dots: [number, number][] = [[250, 44], [430, 76], [590, 84], [800, 50], [260, 560], [430, 588], [620, 575], [820, 605]];
  return <svg className="absolute inset-0 pointer-events-none" width={1000} height={700} aria-hidden="true" data-id="element-3044" data-mp="1rnvjo7mg6mxz">
      {paths.map(d => <path key={d} d={d} fill="none" stroke="#7C75F2" strokeWidth={1.3} opacity={0.75} data-id="element-3045" data-mp="1mrddr4fdsik2" />)}
      {dots.map(([x, y]) => <g key={`${x}-${y}`} data-id="element-3046" data-mp="1hk6nbqpajd43">
          <circle cx={x} cy={y} r={6} fill="#4F46E5" opacity={0.14} data-id="element-3047" data-mp="1hjf4lggsl9v8" />
          <circle cx={x} cy={y} r={2.6} fill="#4F46E5" data-id="element-3048" data-mp="1mjt3a7wi77gl" />
        </g>)}
    </svg>;
}
export function EntryComposition() {
  return <div className="relative w-[1000px] h-[700px]" data-id="element-3049" data-mp="1rhbfc75ti2st">
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse 45% 40% at 50% 55%, rgba(79,70,229,0.08), transparent 70%)'
    }} aria-hidden="true" data-id="element-3050" data-mp="tv48eqm1jyda" />
      <Connectors data-id="element-3051" data-mp="13t1v39o95n3s" />
      <div className="absolute left-[300px] top-0" data-id="element-3052" data-mp="13sidxv6ff2qt"><AskBar width={400} data-id="element-3053" data-mp="13rgxtcajzq28" /></div>
      <div className="absolute left-[73px] top-[108px]" data-id="element-3054" data-mp="18jrzajkxzjww"><EntryLabel entryKey="web" data-id="element-3055" data-mp="18kjye4dqzzrx" /></div>
      <div className="absolute left-[325px] top-[108px]" data-id="element-3056" data-mp="18llb0daewubr"><EntryLabel entryKey="app" width={200} data-id="element-3057" data-mp="b3bhr2ug2p8q" /></div>
      <div className="absolute left-[533px] top-[108px]" data-id="element-3058" data-mp="g3hbqpo6sntz"><EntryLabel entryKey="portal" data-id="element-3059" data-mp="g268qs2kfk3x" /></div>
      <div className="absolute left-[790px] top-[108px]" data-id="element-3060" data-mp="g14ktn0gb34s"><EntryLabel entryKey="llm" width={200} data-id="element-3061" data-mp="81q6rjx1vv36" /></div>
      <div className="absolute left-[10px] top-[258px]" data-id="element-3062" data-mp="33omwwrsvrqn"><Scaled scale={0.57} w={580} h={414} data-id="element-3063" data-mp="32x1ufvtxcem"><WebsiteLaptop data-id="element-3064" data-mp="31lvq9ewvskc" /></Scaled></div>
      <div className="absolute left-[360px] top-[250px]" data-id="element-3065" data-mp="ht49s1o7nd12"><Scaled scale={0.74} w={196} h={424} data-id="element-3066" data-mp="hrtaof5pmtga"><AppPhone data-id="element-3067" data-mp="ctrqfqwjso38" /></Scaled></div>
      <div className="absolute left-[516px] top-[258px]" data-id="element-3068" data-mp="cujd2c863bhd"><Scaled scale={0.62} w={440} h={416} data-id="element-3069" data-mp="3l9bembid3gbf"><PortalScreen stats={PORTAL_STATS} data-id="element-3070" data-mp="3l8i6p77o2krp" /></Scaled></div>
      <div className="absolute left-[800px] top-[270px]" data-id="element-3071" data-mp="3lb4kvpp2byiw"><LlmPanel data-id="element-3072" data-mp="3gb9vxroulj0h" /></div>
      <div className="absolute left-[280px] top-[596px]" data-id="element-3073" data-mp="3gc1he9oqe0d1"><AtnaLayer width={440} data-id="element-3074" data-mp="3v80mdbgjnkdy" /></div>
    </div>;
}